<template>
    <hts-slide-over
        :show="props.show"
        :rules="getRules"
        :title="getAction === 'create' ? $t('labels.addSupply') : $t('labels.supplyEdit')"
        :submit="onSubmit"
        @close="closeOverSide">
        <template #body>
            <div class="hts-vehicles-ignore-add-vehicle">
                <div class="hts-vehicles-ignore-add-vehicle__container-body">
                    <hts-card>
                        <div
                            class="grid gap-4"
                            :class="columnClass">
                            <hts-form-input
                                v-model="dataForm.name"
                                :label="$t('labels.supplyList.name')"
                                prop="supplyName"
                                name="typeSupplyName"
                                :color="props.color" />
                            <hts-form-input
                                v-model="dataForm.price"
                                :label="$t('labels.supplyList.price')"
                                :required="true"
                                :color="props.color"
                                prop="price"
                                name="price"
                                type="number" />
                            <hts-form-select
                                v-model="dataForm.type"
                                :label="$t('labels.supplyList.type')"
                                required
                                :items="optionType"
                                key-id="code"
                                key-label="name"
                                :color="props.color"
                                prop="type"
                                name="type" />
                            <hts-form-select
                                v-model="dataForm.subType"
                                :label="$t('labels.supplyList.subType')"
                                required
                                :items="optionSubType"
                                key-id="code"
                                key-label="name"
                                :color="props.color"
                                prop="subType"
                                name="subType" />
                            <hts-form-select
                                v-model="dataForm.status"
                                :label="$t('labels.supplyList.status')"
                                :items="selectOptionStatus"
                                key-id="code"
                                key-label="name"
                                :color="props.color"
                                prop="status"
                                name="status" />
                            <hts-form-input
                                v-model="dataForm.vendor"
                                :label="$t('labels.supplyList.vendor')"
                                :color="props.color"
                                prop="vendor"
                                name="vendor" />
                            <hts-form-input
                                v-model="dataForm.weightKG"
                                :label="$t('labels.supplyList.weight')"
                                :color="props.color"
                                prop="weightKG"
                                name="weightKG"
                                type="number" />
                            <hts-form-input
                                v-model="dataForm.description"
                                :label="$t('labels.supplyList.description')"
                                :color="props.color"
                                prop="description"
                                name="description" />
                            <hts-form-input
                                v-if="dataForm.type === ESupplyType.REPAIR_LARGE || dataForm.type === ESupplyType.ASSETS_LARGE"
                                v-model="dataForm.depreciationMonth"
                                :label="$t('labels.supplyList.depreciationMonth')"
                                required
                                :color="props.color"
                                prop="depreciationMonth"
                                name="depreciationMonth"
                                type="number" />
                            <hts-form-input
                                v-if="dataForm.type === ESupplyType.REPAIR_LARGE || dataForm.type === ESupplyType.ASSETS_LARGE"
                                v-model="dataForm.priceLiquidation"
                                :label="$t('labels.supplyList.priceLiquidation')"
                                required
                                :color="props.color"
                                prop="priceLiquidation"
                                name="priceLiquidation"
                                type="number" />
                            <hts-form-input
                                v-if="dataForm.type === ESupplyType.FUEL"
                                v-model="dataForm.usedTotal"
                                :label="$t('labels.supplyList.usedTotal')"
                                required
                                :color="props.color"
                                prop="usedTotal"
                                name="usedTotal"
                                type="number" />
                        </div>
                    </hts-card>
                </div>
            </div>
        </template>
    </hts-slide-over>
</template>

<script setup lang="ts">
import { ISupply, ESupplyType, ESupplySubType, ESupplyStatus } from '../models/supply'
const props = defineProps({
  show: {
    type: Boolean,
    default: false,
    required: true
  },
  color: {
    type: String,
    default: 'theme'
  },
  sizeItemInput: {
    type: String,
    default: 'small'
  },
  column: {
    type: Number,
    default: 1
  },
  supplyData: {
    type: Object as () => ISupply,
    required: true
  },
  action: {
    type: String,
    default: 'create',
    required: true
  }
})

const emits = defineEmits(['close', 'onSubmit'])

const closeOverSide = () => {
  emits('close')
}

const getAction = computed(() => props.action)

const dataForm: ISupply = reactive({ ...props.supplyData })

const getRules = computed(() => {
  if (dataForm.type === ESupplyType.FUEL) {
    return {
      price: 'required',
      type: 'required',
      subType: 'required',
      usedTotal: 'required'
    }
  }
  if (dataForm.type === ESupplyType.REPAIR_LARGE || dataForm.type === ESupplyType.ASSETS_LARGE) {
    return {
      price: 'required',
      type: 'required',
      subType: 'required',
      depreciationMonth: 'required',
      priceLiquidation: 'required'
    }
  }
  if (dataForm.type === ESupplyType.REPAIR_SMALL || dataForm.type === ESupplyType.ASSETS_SMALL) {
    return {
      price: 'required',
      type: 'required',
      subType: 'required'
    }
  }
})

const optionType = [
  {
    code: ESupplyType.FUEL,
    name: $t('labels.supplyList.fuel')
  },
  {
    code: ESupplyType.REPAIR_LARGE,
    name: $t('labels.supplyList.repairLarge')
  },
  {
    code: ESupplyType.REPAIR_SMALL,
    name: $t('labels.supplyList.repairSmall')
  },
  {
    code: ESupplyType.ASSETS_LARGE,
    name: $t('labels.supplyList.assetsLagre')
  },
  {
    code: ESupplyType.ASSETS_SMALL,
    name: $t('labels.supplyList.assetsSmall')
  }
]

const optionSubType = [
  {
    code: ESupplySubType.MOOC,
    name: $t('labels.supplyList.mooc')
  },
  {
    code: ESupplySubType.OIL,
    name: $t('labels.supplyList.oil')
  },
  {
    code: ESupplySubType.WHEEL,
    name: $t('labels.supplyList.wheel')
  },
  {
    code: ESupplySubType.GENERATOR,
    name: $t('labels.supplyList.generator')
  },
  {
    code: ESupplySubType.CONTAINER,
    name: $t('labels.supplyList.container')
  },
  {
    code: ESupplySubType.CHARGE,
    name: $t('labels.supplyList.charge')
  }
]

const selectOptionStatus = [
  {
    code: ESupplyStatus.DEFAULT,
    name: $t('labels.supplyList.default')
  },
  {
    code: ESupplyStatus.INACTIVE,
    name: $t('labels.supplyList.inactive')
  },
  {
    code: ESupplyStatus.ACTIVE,
    name: $t('labels.supplyList.active')
  },
  {
    code: ESupplyStatus.DISABLE,
    name: $t('labels.supplyList.disable')
  }
]

const cleanData = (data: any) => {
  const temp = data
  if (dataForm.type === ESupplyType.REPAIR_LARGE || dataForm.type === ESupplyType.ASSETS_LARGE) {
    delete temp.usedTotal
  }
  if (dataForm.type === ESupplyType.FUEL) {
    delete temp.depreciationMonth
    delete temp.priceLiquidation
  }
  if (dataForm.type === ESupplyType.REPAIR_SMALL || dataForm.type === ESupplyType.ASSETS_SMALL) {
    delete temp.usedTotal
    delete temp.depreciationMonth
    delete temp.priceLiquidation
  }
  if (getAction.value === 'create') {
    delete temp.ID
  }
  for (const propName in temp) {
    if (temp[propName] === '') {
      delete temp[propName]
    }
  }
  return temp
}

const onSubmit = () => {
  const data = cleanData(dataForm)
  emits('onSubmit', data, props.action)
}

const columnClass = computed(() => {
  if ($lodash.inRange(props.column, 1, 12)) {
    return `grid-cols-${props.column}`
  }

  return 'grid-cols-1'
})
</script>
